import CryptoJS from 'crypto-js';

/**
 * Servicio de Encriptación para localStorage
 * Protege datos sensibles almacenados localmente
 */
class EncryptionService {
  constructor() {
    this.secretKey = process.env.REACT_APP_ENCRYPTION_KEY || 'fallback-encryption-key';
    this.storagePrefix = 'ams_encrypted_';
  }

  /**
   * Encripta datos para almacenamiento
   * @param {any} data - Datos a encriptar
   * @returns {string} Datos encriptados
   */
  encrypt(data) {
    try {
      const jsonString = JSON.stringify(data);
      const encrypted = CryptoJS.AES.encrypt(jsonString, this.secretKey).toString();
      return encrypted;
    } catch (error) {
      console.error('Error al encriptar datos:', error);
      throw new Error('Error en encriptación de datos');
    }
  }

  /**
   * Desencripta datos almacenados
   * @param {string} encryptedData - Datos encriptados
   * @returns {any} Datos desencriptados
   */
  decrypt(encryptedData) {
    try {
      if (!encryptedData) return null;
      
      const decrypted = CryptoJS.AES.decrypt(encryptedData, this.secretKey);
      const jsonString = decrypted.toString(CryptoJS.enc.Utf8);
      
      if (!jsonString) return null;
      
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('Error al desencriptar datos:', error);
      return null;
    }
  }

  /**
   * Guarda datos encriptados en localStorage
   * @param {string} key - Clave de almacenamiento
   * @param {any} data - Datos a guardar
   */
  setItem(key, data) {
    try {
      const encryptedData = this.encrypt(data);
      const storageKey = this.storagePrefix + key;
      localStorage.setItem(storageKey, encryptedData);
    } catch (error) {
      console.error('Error al guardar datos encriptados:', error);
      throw new Error('Error al guardar datos seguros');
    }
  }

  /**
   * Obtiene datos desencriptados de localStorage
   * @param {string} key - Clave de almacenamiento
   * @returns {any} Datos desencriptados o null
   */
  getItem(key) {
    try {
      const storageKey = this.storagePrefix + key;
      const encryptedData = localStorage.getItem(storageKey);
      
      if (!encryptedData) return null;
      
      return this.decrypt(encryptedData);
    } catch (error) {
      console.error('Error al obtener datos encriptados:', error);
      return null;
    }
  }

  /**
   * Elimina datos encriptados de localStorage
   * @param {string} key - Clave de almacenamiento
   */
  removeItem(key) {
    try {
      const storageKey = this.storagePrefix + key;
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error('Error al eliminar datos encriptados:', error);
    }
  }

  /**
   * Limpia todos los datos encriptados
   */
  clear() {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.storagePrefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error al limpiar datos encriptados:', error);
    }
  }

  /**
   * Verifica integridad de datos encriptados
   * @param {string} key - Clave a verificar
   * @returns {boolean} True si los datos son válidos
   */
  verifyIntegrity(key) {
    try {
      const data = this.getItem(key);
      return data !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Migra datos no encriptados a formato encriptado
   * @param {string} key - Clave a migrar
   */
  migrateData(key) {
    try {
      // Buscar datos no encriptados
      const unencryptedData = localStorage.getItem(key);
      
      if (unencryptedData && !localStorage.getItem(this.storagePrefix + key)) {
        try {
          const data = JSON.parse(unencryptedData);
          this.setItem(key, data);
          localStorage.removeItem(key); // Eliminar versión no encriptada
          console.log(`Datos migrados a formato encriptado: ${key}`);
        } catch (parseError) {
          console.warn(`No se pudo migrar ${key}, formato inválido`);
        }
      }
    } catch (error) {
      console.error('Error en migración de datos:', error);
    }
  }

  /**
   * Genera hash de verificación para datos
   * @param {any} data - Datos a hashear
   * @returns {string} Hash de verificación
   */
  generateHash(data) {
    try {
      const jsonString = JSON.stringify(data);
      return CryptoJS.SHA256(jsonString).toString();
    } catch (error) {
      console.error('Error al generar hash:', error);
      return '';
    }
  }

  /**
   * Verifica hash de datos
   * @param {any} data - Datos a verificar
   * @param {string} expectedHash - Hash esperado
   * @returns {boolean} True si coinciden
   */
  verifyHash(data, expectedHash) {
    try {
      const currentHash = this.generateHash(data);
      return currentHash === expectedHash;
    } catch (error) {
      console.error('Error al verificar hash:', error);
      return false;
    }
  }
}

// Exportar instancia singleton
const encryptionService = new EncryptionService();
export default encryptionService;